import React, { useState, useEffect } from 'react';
import { Star, ThumbsUp, MessageCircle, Filter, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const UserReviews = ({ providerId }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    rating: 'all',
    timeRange: 'all'
  });

  useEffect(() => {
    // Mock reviews data since we don't have a reviews table in schema
    setReviews([
      {
        id: '1',
        user_name: 'John D.',
        rating: 4,
        comment: 'Generally reliable service, but recent outages have been concerning.',
        created_at: '2025-01-25T10:00:00Z',
        helpful_count: 5
      },
      {
        id: '2',
        user_name: 'Sarah M.',
        rating: 3,
        comment: 'Service is okay but support response time could be better.',
        created_at: '2025-01-20T14:30:00Z',
        helpful_count: 2
      },
      {
        id: '3',
        user_name: 'Mike R.',
        rating: 5,
        comment: 'Excellent uptime and customer service. Highly recommended!',
        created_at: '2025-01-15T09:15:00Z',
        helpful_count: 8
      }
    ]);
  }, [providerId]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const filteredReviews = reviews?.filter(review => {
    if (filters?.rating !== 'all' && review?.rating !== parseInt(filters?.rating)) {
      return false;
    }
    
    if (filters?.timeRange !== 'all') {
      const days = { '7d': 7, '30d': 30, '90d': 90 };
      const cutoffDate = new Date();
      cutoffDate?.setDate(cutoffDate?.getDate() - days?.[filters?.timeRange]);
      
      if (new Date(review?.created_at) < cutoffDate) {
        return false;
      }
    }
    
    return true;
  });

  const averageRating = reviews?.length > 0 
    ? (reviews?.reduce((sum, review) => sum + review?.rating, 0) / reviews?.length)?.toFixed(1)
    : 0;

  const ratingDistribution = [5, 4, 3, 2, 1]?.map(rating => ({
    rating,
    count: reviews?.filter(review => review?.rating === rating)?.length,
    percentage: reviews?.length > 0 
      ? (reviews?.filter(review => review?.rating === rating)?.length / reviews?.length) * 100 
      : 0
  }));

  const StarRating = ({ rating, size = 'sm' }) => (
    <div className="flex items-center">
      {[1, 2, 3, 4, 5]?.map((star) => (
        <Star
          key={star}
          className={`${size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'} ${
            star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
          }`}
        />
      ))}
    </div>
  );

  const ratingOptions = [
    { value: 'all', label: 'All Ratings' },
    { value: '5', label: '5 Stars' },
    { value: '4', label: '4 Stars' },
    { value: '3', label: '3 Stars' },
    { value: '2', label: '2 Stars' },
    { value: '1', label: '1 Star' }
  ];

  const timeRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' }
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Rating Summary */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">User Ratings</h3>
        
        <div className="text-center mb-6">
          <div className="text-4xl font-bold text-gray-900 mb-2">{averageRating}</div>
          <StarRating rating={Math.round(parseFloat(averageRating))} />
          <p className="text-sm text-gray-500 mt-2">{reviews?.length} reviews</p>
        </div>

        <div className="space-y-2">
          {ratingDistribution?.map(({ rating, count, percentage }) => (
            <div key={rating} className="flex items-center">
              <span className="text-sm text-gray-600 w-8">{rating}</span>
              <Star className="h-4 w-4 text-yellow-400 fill-current mr-2" />
              <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                <div
                  className="bg-yellow-400 h-2 rounded-full"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="text-sm text-gray-500 w-8">{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Reviews List */}
      <div className="lg:col-span-3">
        <div className="bg-white rounded-lg shadow-sm">
          {/* Header and Filters */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 sm:mb-0">
                Reviews ({filteredReviews?.length})
              </h3>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
                <ChevronDown className={`h-4 w-4 ml-2 transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </Button>
            </div>

            {showFilters && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Select
                  value={filters?.rating}
                  onValueChange={(value) => handleFilterChange('rating', value)}
                  placeholder="Filter by rating"
                >
                  {ratingOptions?.map((option) => (
                    <option key={option?.value} value={option?.value}>
                      {option?.label}
                    </option>
                  ))}
                </Select>

                <Select
                  value={filters?.timeRange}
                  onValueChange={(value) => handleFilterChange('timeRange', value)}
                  placeholder="Filter by time"
                >
                  {timeRangeOptions?.map((option) => (
                    <option key={option?.value} value={option?.value}>
                      {option?.label}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </div>

          {/* Reviews */}
          <div className="divide-y divide-gray-200">
            {filteredReviews?.length > 0 ? (
              filteredReviews?.map((review) => (
                <div key={review?.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className="font-medium text-gray-900 mr-3">
                          {review?.user_name}
                        </span>
                        <StarRating rating={review?.rating} />
                        <span className="text-sm text-gray-500 ml-3">
                          {format(new Date(review?.created_at), 'MMM dd, yyyy')}
                        </span>
                      </div>
                      
                      <p className="text-gray-700 leading-relaxed mb-4">
                        {review?.comment}
                      </p>

                      <div className="flex items-center space-x-4">
                        <button className="flex items-center text-sm text-gray-500 hover:text-gray-700">
                          <ThumbsUp className="h-4 w-4 mr-1" />
                          Helpful ({review?.helpful_count})
                        </button>
                        
                        <button className="flex items-center text-sm text-gray-500 hover:text-gray-700">
                          <MessageCircle className="h-4 w-4 mr-1" />
                          Reply
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-12 text-center">
                <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews found</h3>
                <p className="text-gray-500">
                  {reviews?.length === 0 
                    ? 'Be the first to review this provider!' :'No reviews match your current filters.'
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserReviews;